import { useMemo, useState } from 'react';
import { Check, Copy, Download } from 'lucide-react';
import type { ArtifactDocumentViewData } from '@shared/types/views';
import { Button } from '@/components/ui/Button';
import { MarkdownRenderer } from '@/components/execution/renderers/MarkdownRenderer';
import { ViewActionButton } from '@/components/views/atoms/ViewActionButton';
import { sweepMarkdownHeadings, sweepVisibleHeading } from '@/lib/ui-editorial-sweeper';
import { cn } from '@/lib/utils';

interface ArtifactDocumentViewProps {
  data: ArtifactDocumentViewData;
  hideTitle?: boolean;
}

type CopyState = 'idle' | 'copied' | 'failed';

const DEFAULT_EXPORT_ACTIONS: NonNullable<ArtifactDocumentViewData['export_actions']> = [
  'copy_markdown',
];

const normalizeComparableText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[#*_`>[\]()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const isArtifactSubjectRepeatedInBodyLead = (
  subject: string,
  body: string
): boolean => {
  const normalizedSubject = normalizeComparableText(subject);
  if (!normalizedSubject) return false;

  const bodyLead = body
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0)
    .slice(0, 4)
    .join(' ');

  return normalizeComparableText(bodyLead).includes(normalizedSubject);
};

const getVisibleArtifactMetaItems = (
  data: ArtifactDocumentViewData
): ArtifactDocumentViewData['meta_items'] => {
  const title = normalizeComparableText(data.title);
  const subject = normalizeComparableText(data.subject);

  return data.meta_items.filter((item) => {
    const value = normalizeComparableText(item.value);
    return value !== title && value !== subject && !['etapes', 'sources', 'tache'].includes(normalizeComparableText(item.label));
  });
};

const slugifyFilename = (value: string): string => {
  const normalized = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || 'document-domino';
};

const buildMarkdownDocument = (data: ArtifactDocumentViewData): string => {
  const metaLines = getVisibleArtifactMetaItems(data).map(
    (item) => `- **${item.label}**: ${item.value}`
  );
  const headerLines = [`# ${sweepVisibleHeading(data.title)}`];

  if (!isArtifactSubjectRepeatedInBodyLead(data.subject, data.body)) {
    headerLines.push('', `**Objet**: ${data.subject}`);
  }

  if (metaLines.length > 0) {
    headerLines.push('', ...metaLines);
  }

  return [
    ...headerLines,
    '',
    '---',
    '',
    prioritizeDecisionSection(sweepMarkdownHeadings(data.body)).trim(),
    '',
  ].join('\n');
};

export const prioritizeDecisionSection = (markdown: string): string => {
  const lines = markdown.split(/\r?\n/);
  const sectionLabel = (line: string): string | null => {
    const heading = /^#{1,6}\s+(.+?)\s*$/.exec(line)?.[1];
    if (heading) return heading;
    return /^\s*\*\*(.+?)\*\*\s*$/.exec(line)?.[1] ?? null;
  };
  const starts = lines
    .map((line, index) => (sectionLabel(line) ? index : -1))
    .filter((index) => index >= 0);
  if (starts.length < 2) return markdown;
  const sections = starts.map((start, index) => ({
    start,
    lines: lines.slice(start, starts[index + 1] ?? lines.length),
  }));
  const decisionIndex = sections.findIndex((section) => {
    const heading = normalizeComparableText(sectionLabel(section.lines[0] ?? '') ?? '');
    return /^(decision|recommandation|verdict|conclusion|suite proposee)\b/i.test(heading);
  });
  if (decisionIndex <= 0) return markdown;
  const preamble = lines.slice(0, starts[0]);
  const decision = sections[decisionIndex];
  if (!decision) return markdown;
  return [
    ...preamble,
    ...decision.lines,
    ...sections.filter((_, index) => index !== decisionIndex).flatMap((section) => section.lines),
  ].join('\n');
};

const copyTextToClipboard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
};

export const ArtifactDocumentView = ({
  data,
  hideTitle = false,
}: ArtifactDocumentViewProps) => {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const markdownDocument = useMemo(() => buildMarkdownDocument(data), [data]);
  const exportActions = data.export_actions ?? DEFAULT_EXPORT_ACTIONS;
  const shouldRenderMarkdown = data.body_format !== 'plain_text';
  const showSubject = !isArtifactSubjectRepeatedInBodyLead(data.subject, data.body);
  const visibleMetaItems = useMemo(() => getVisibleArtifactMetaItems(data), [data]);
  const visibleBody = useMemo(() => prioritizeDecisionSection(sweepMarkdownHeadings(data.body)), [data.body]);
  const showHeader = !hideTitle || visibleMetaItems.length > 0 || Boolean(data.primaryAction);

  const handleCopyMarkdown = async () => {
    try {
      await copyTextToClipboard(markdownDocument);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 1600);
    } catch {
      setCopyState('failed');
      window.setTimeout(() => setCopyState('idle'), 2200);
    }
  };

  const handleDownloadMarkdown = () => {
    const blob = new Blob([markdownDocument], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = `${slugifyFilename(data.title)}.md`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const renderExportActions = () => (
    <div className="flex flex-wrap gap-2">
      {exportActions.includes('copy_markdown') && (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="gap-2"
          title="Copier le document en Markdown"
          onClick={handleCopyMarkdown}
        >
          {copyState === 'copied' ? (
            <Check className="h-4 w-4" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
          {copyState === 'failed'
            ? 'Copie impossible'
            : copyState === 'copied'
              ? 'Copié'
              : 'Copier'}
        </Button>
      )}

      {exportActions.includes('download_markdown') && (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="gap-2"
          title="Télécharger le document en Markdown"
          onClick={handleDownloadMarkdown}
        >
          <Download className="h-4 w-4" />
          Télécharger
        </Button>
      )}
    </div>
  );

  return (
    <div data-view-surface className="rounded-2xl border border-domino-cardBorder bg-domino-white p-5">
      {showHeader && (
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 max-w-4xl">
            {!hideTitle && (
              <p className="text-xl font-semibold tracking-tight text-domino-ink">
                {sweepVisibleHeading(data.title)}
              </p>
            )}
            {visibleMetaItems.length > 0 && (
              <dl className={cn('flex flex-wrap gap-x-6 gap-y-1 text-sm', !hideTitle && 'mt-3')}>
                {visibleMetaItems.map((item) => (
                  <div
                    key={`${item.label}:${item.value}`}
                    className="flex items-baseline gap-2"
                  >
                    <dt className="text-domino-inkSoft">{item.label}</dt>
                    <dd className="font-medium text-domino-ink">{item.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>

          {data.primaryAction && (
            <div className="shrink-0">
              <ViewActionButton action={data.primaryAction} />
            </div>
          )}
        </div>
      )}

      {exportActions.length > 0 && (
        <div className="mt-4">{renderExportActions()}</div>
      )}

      {showSubject && (
        <div
          className={cn(
            'flex items-baseline gap-3',
            (!hideTitle || showHeader) &&
              'mt-5 border-t border-domino-cardBorder pt-4'
          )}
        >
          <p className="text-sm text-domino-inkSoft">Objet</p>
          <p className="text-base font-medium text-domino-ink">{data.subject}</p>
        </div>
      )}

      <div
        className={cn(
          showSubject
            ? 'mt-4 border-t border-domino-cardBorder pt-5'
            : (!hideTitle || showHeader) &&
                'mt-5 border-t border-domino-cardBorder pt-5'
        )}
      >
        <div className="text-base leading-7 text-domino-ink">
          {shouldRenderMarkdown ? (
            <MarkdownRenderer content={visibleBody} />
          ) : (
            <div className="whitespace-pre-wrap">{visibleBody}</div>
          )}
        </div>
      </div>

    </div>
  );
};
